/**
 * The `macroReleases` section — "The releases, grouped" (`docs/plans/macro.md`;
 * mock `docs/mockups/macro-v2.html`, section 01, `glEco`).
 *
 * Five groups side by side with vertical dividers — Growth · Inflation · Labor ·
 * Policy & rates · Sentiment. A tile is one series: the last reading, ▲▼ and the
 * change coloured by **better or worse for the economy** (not up or down), the
 * last 12 readings as columns, and a badge saying where the numbers come from.
 * Hovering a column reads that reading off in the tile's foot.
 *
 * The rules are `lib/macro-releases.ts`; the data `api/macro-releases.ts`, the
 * admin-only `GET /macro/series`. When that read has nothing (503 before the
 * artifact is built), the payload's note is shown in the head.
 * `MacroReleasesConfig` has no fields yet, so nothing here is persisted.
 */

import { useEffect, useState, type CSSProperties } from "react";
import { fetchMacroReleases, type MacroReleasesPayload } from "../../api/macro-releases";
import {
  releaseArrow,
  releaseGroups,
  formatValue,
  type ReleaseColumn,
  type ReleaseState,
  type ReleaseTile,
  type ReleaseTone,
} from "../../lib/macro-releases";
import { MACRO_KIND_TITLE, macroScale } from "../../lib/macro-sections";
import type { MacroSectionProps } from "./types";

/** The app's own font (Inter), not a monospace (owner, 2026-11-02); numbers line up via tabular-nums on the host. */
const MONO = "inherit";

/** Each data state's colour token — `stored` is green, the Massive feeds purple. */
const STATE_TOKEN: Record<ReleaseState, string> = {
  stored: "var(--qf-positive)",
  massive: "var(--qf-accent-purple)",
  derive: "var(--qf-warn)",
  pending: "var(--qf-text-muted)",
};

const TONE_TOKEN: Record<ReleaseTone, string> = {
  better: "var(--qf-positive)",
  worse: "var(--qf-negative)",
  neutral: "var(--qf-text-muted)",
};

const COLUMN_TOKEN: Record<ReleaseColumn["kind"], string> = {
  last: "var(--qf-accent)",
  neg: "var(--qf-negative)",
  pos: "var(--qf-accent-blue)",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `2026-09-01` → `Sep 2026`. Strings only — no `Date`, so no time zone. */
export function monthLabel(iso: string): string {
  const [y, m] = iso.split("-");
  const name = MONTHS[Number(m) - 1];
  return name && y ? `${name} ${y}` : iso;
}

export function StateBadge({ state }: { state: ReleaseState }) {
  const c = STATE_TOKEN[state];
  return (
    <span
      data-state={state}
      style={{
        display: "inline-block",
        fontFamily: MONO,
        fontSize: "10.5px",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "0 6px",
        borderRadius: 3,
        border: `1px solid color-mix(in srgb, ${c} 45%, transparent)`,
        background: `color-mix(in srgb, ${c} 8%, transparent)`,
        color: c,
        whiteSpace: "nowrap",
      }}
    >
      {state}
    </span>
  );
}

function Tile({ tile, scale }: { tile: ReleaseTile; scale: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const { spec } = tile;
  const hovered = hover === null ? undefined : tile.columns[hover];
  return (
    // No source badge and no source tooltip: where a number comes from (and the
    // vendor's name) is ours, not the reader's (owner, 2026-11-01).
    <div data-tile={spec.id} data-status={tile.status} style={S.tile}>
      {/* The label and the reading share one row (owner, 2026-11-01): label left,
          value and change right, so a tile is a row shorter. */}
      <div data-tile-row="" style={S.tileRow}>
        <span style={S.tileHead}>{spec.label}</span>
        {tile.status === "ready" ? (
          <span style={S.reading}>
            <span style={S.value}>{tile.value}</span>
            {tile.change && (
              <span data-tone={tile.tone} style={{ ...S.change, color: TONE_TOKEN[tile.tone ?? "neutral"] }}>
                {releaseArrow(tile.change.sign)} {tile.change.text}
              </span>
            )}
          </span>
        ) : (
          <span style={{ ...S.value, color: "var(--qf-text-muted)" }}>—</span>
        )}
      </div>
      {tile.status === "ready" ? (
        <>
          <svg
            viewBox="0 0 160 36"
            preserveAspectRatio="none"
            data-columns=""
            style={{ width: "100%", height: Math.round(36 * scale), display: "block" }}
            onMouseLeave={() => setHover(null)}
          >
            {tile.columns.map((c, i) => (
              <rect
                key={c.point.date}
                x={c.x}
                y={c.y}
                width={c.w}
                height={c.h}
                fill={COLUMN_TOKEN[c.kind]}
                opacity={c.kind === "last" || hover === i ? 1 : 0.55}
                onMouseEnter={() => setHover(i)}
              />
            ))}
          </svg>
          <div style={S.foot}>
            {hovered
              ? `${monthLabel(hovered.point.date)} · ${formatValue(spec.unit, hovered.point)}`
              : tile.asOf
                ? `as of ${monthLabel(tile.asOf)}`
                : ""}
          </div>
        </>
      ) : (
        <div style={S.missing}>
          <span style={S.foot}>
            {tile.status === "pending" ? spec.from : "No readings yet"}
          </span>
        </div>
      )}
    </div>
  );
}

export default function MacroReleasesSection({ section, config }: MacroSectionProps<"macroReleases">) {
  // The stacked layout's drag-to-resize: the column charts' height times this
  // (`lib/macro-sections.ts`); text is not scaled.
  const scale = macroScale(config);
  const [payload, setPayload] = useState<MacroReleasesPayload | null>(null);

  useEffect(() => {
    let live = true;
    fetchMacroReleases()
      .then((p) => live && setPayload(p))
      .catch(() => live && setPayload({ series: {}, note: "Macro series unavailable" }));
    return () => {
      live = false;
    };
  }, []);

  const groups = releaseGroups(payload?.series ?? {});

  return (
    <div data-macro-kind="macroReleases" style={S.root}>
      <div style={S.head}>
        <h2 style={S.title}>{section.title ?? MACRO_KIND_TITLE.macroReleases}</h2>
        {payload?.note && (
          <span role="status" style={S.note}>
            {payload.note}
          </span>
        )}
      </div>
      <p style={S.prose}>
        Last 12 readings as columns. The arrow is coloured by better or worse for the economy, not by up
        or down.
      </p>
      <div style={S.groups}>
        {groups.map(({ group, tiles }, i) => (
          <div key={group} data-group={group} style={i === 0 ? S.group : { ...S.group, ...S.divided }}>
            <div style={S.groupHead}>{group}</div>
            {tiles.map((t) => (
              <Tile key={t.spec.id} tile={t} scale={scale} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  root: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "var(--qf-surface)",
    color: "var(--qf-text)",
    boxSizing: "border-box",
  },
  head: { display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" },
  title: { margin: 0, fontSize: 16, fontWeight: 600 },
  note: {
    fontFamily: MONO,
    fontSize: "10.5px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "0 6px",
    borderRadius: 3,
    border: "1px solid color-mix(in srgb, var(--qf-warn) 45%, transparent)",
    color: "var(--qf-warn)",
  },
  prose: { margin: 0, fontSize: 13.5, color: "var(--qf-text-secondary)" },
  groups: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  group: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  divided: { borderLeft: "1px solid var(--qf-border)", paddingLeft: 14 },
  groupHead: {
    fontFamily: MONO,
    fontSize: "11.5px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--qf-text-secondary)",
    margin: "6px 0 4px",
  },
  tile: {
    background: "var(--qf-background)",
    border: "1px solid var(--qf-border)",
    borderRadius: 5,
    padding: "6px 9px",
  },
  tileRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, minWidth: 0 },
  reading: { display: "flex", alignItems: "baseline", gap: 8, whiteSpace: "nowrap", flex: "0 0 auto" },
  tileHead: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    fontFamily: MONO,
    fontSize: "10.5px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "var(--qf-text-muted)",
  },
  value: { fontFamily: MONO, fontSize: 17, fontWeight: 600 },
  change: { fontFamily: MONO, fontSize: 12 },
  foot: { fontFamily: MONO, fontSize: 11, color: "var(--qf-text-muted)", minHeight: 14 },
  missing: { display: "flex", alignItems: "baseline", gap: 8, minHeight: 36 + 14 },
};
